import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { Sparkles } from 'lucide-react';
import mcpServerService from '../../../services/mcpServerService';
import sharedStyles from '../../PlatformAdmin/PlatformAdmin.module.scss';
import styles from '../McpServerDetail.module.scss';
import dialogStyles from '../EnrichDiffDialog.module.scss';
import { describeError } from '../mcpUtils';

const keyOf = (change, index) => change.tool_id ?? change.tool_name ?? index;

/**
 * Description enrichment (§6).
 *
 * `POST /enrich` returns a diff and writes nothing. Applying is per-row on
 * purpose: descriptions are the single biggest factor in whether a model calls
 * a tool correctly, and an LLM silently rewriting all of them can make a
 * working connector worse. Descriptions the customer edited by hand are never
 * sent for rewriting — they come back in `skipped`.
 */
const EnrichDiffDialog = ({ open, server, onClose, onApplied, notify }) => {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [accepted, setAccepted] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      setResult(null);
      setAccepted(new Set());
      try {
        const data = await mcpServerService.enrich(server.id);
        if (cancelled) return;
        setResult(data);
        // Nothing is pre-accepted — the point of the screen is the decision.
        setAccepted(new Set());
      } catch (e) {
        if (!cancelled) setError(describeError(e, "Couldn't generate suggestions."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, server.id]);

  const changes = result?.changes || [];

  const toggle = (key) =>
    setAccepted((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const handleApply = async () => {
    const chosen = changes.filter((c, i) => accepted.has(keyOf(c, i)));
    if (chosen.length === 0) return;
    setApplying(true);
    try {
      await mcpServerService.applyEnrichment(server.id, chosen);
      notify?.(
        `${chosen.length} ${chosen.length === 1 ? 'description' : 'descriptions'} updated.`,
        'success'
      );
      await onApplied?.();
    } catch (e) {
      setError(describeError(e, "Couldn't apply the changes."));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={applying ? undefined : onClose}
      maxWidth={false}
      fullWidth
      scroll="paper"
      className={dialogStyles.dialog}
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(24, 24, 27, 0.4)', backdropFilter: 'blur(2px)' } },
      }}
      PaperProps={{ className: dialogStyles.dialogPaper }}
    >
      <DialogTitle className={dialogStyles.dialogTitle} component="div">
        <Box className={dialogStyles.titleRow}>
          <Sparkles size={19} />
          <Typography component="h3" variant="h6" className={dialogStyles.title}>
            Improve descriptions
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" className={dialogStyles.closeButton} aria-label="Close">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent className={dialogStyles.dialogContent}>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 6, justifyContent: 'center' }}>
            <CircularProgress size={22} />
            <span className={sharedStyles.muted}>
              Writing suggestions — this can take a minute on a large server.
            </span>
          </Box>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {!loading && result && (
          <>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <span className={sharedStyles.muted}>
                {changes.length} {changes.length === 1 ? 'suggestion' : 'suggestions'}
                {result.skipped ? ` · ${result.skipped} skipped (you edited those by hand)` : ''}
                {result.model ? ` · ${result.model}` : ''}
              </span>
              <Box sx={{ flex: 1 }} />
              {changes.length > 0 && (
                <>
                  <Button
                    className={sharedStyles.subtleBtn}
                    size="small"
                    onClick={() => setAccepted(new Set())}
                  >
                    Reject all
                  </Button>
                  <Button
                    className={sharedStyles.outlineBtn}
                    variant="outlined"
                    size="small"
                    onClick={() => setAccepted(new Set(changes.map(keyOf)))}
                  >
                    Accept all
                  </Button>
                </>
              )}
            </Box>

            {changes.length === 0 ? (
              <Alert severity="info">
                Nothing to suggest — every description is either already good or was written by you.
              </Alert>
            ) : (
              <div className={sharedStyles.tableCard}>
                {changes.map((change, index) => {
                  const key = keyOf(change, index);
                  const isAccepted = accepted.has(key);
                  const params = change.proposed_parameters || {};

                  return (
                    <div key={key} className={styles.diffRow}>
                      <div className={styles.diffCol}>
                        <span className={styles.diffColLabel}>
                          {change.tool_name} — current
                        </span>
                        <span
                          className={`${styles.diffText} ${
                            change.current_description ? '' : styles.diffTextEmpty
                          }`}
                        >
                          {change.current_description || 'No description'}
                        </span>
                      </div>

                      <div className={styles.diffCol}>
                        <span className={styles.diffColLabel}>Proposed</span>
                        <span className={`${styles.diffText} ${styles.diffTextProposed}`}>
                          {change.proposed_description}
                        </span>
                        {Object.keys(params).length > 0 && (
                          <ul className={styles.diffParams}>
                            {Object.entries(params).map(([name, desc]) => (
                              <li key={name}>
                                <span className={styles.diffParamName}>{name}</span> — {desc}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      <Checkbox
                        checked={isAccepted}
                        onChange={() => toggle(key)}
                        size="small"
                        title={isAccepted ? 'Will be applied' : 'Will be left alone'}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            <span className={styles.sectionHint}>
              This runs on your own LLM key and shows in usage analytics as{' '}
              <code>mcp_generation</code>.
            </span>
          </>
        )}
      </DialogContent>

      <DialogActions className={dialogStyles.dialogActions}>
        <Button onClick={onClose} variant="text" className={dialogStyles.cancelButton} disabled={applying}>
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          className={dialogStyles.saveButton}
          disabled={applying || accepted.size === 0}
          startIcon={applying ? <CircularProgress size={15} color="inherit" /> : null}
        >
          {applying
            ? 'Applying…'
            : accepted.size === 0
            ? 'Select changes to apply'
            : `Apply ${accepted.size} ${accepted.size === 1 ? 'change' : 'changes'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EnrichDiffDialog;
